<template><router-view /></template>
<script setup>
import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
let revealObserver;

const jumpToTop = () => {
  const root = document.documentElement;
  const previous = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  window.scrollTo(0, 0);
  requestAnimationFrame(() => { root.style.scrollBehavior = previous; });
};

const bindScrollReveal = () => {
  revealObserver?.disconnect();
  revealObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('site-reveal--visible');
        revealObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

  document.querySelectorAll('.page-shell main > section, .page-shell.home > section:not(.hero)').forEach(el => {
    el.classList.add('site-reveal');
    revealObserver.observe(el);
  });
};

const refreshPage = async () => {
  jumpToTop();
  await nextTick();
  requestAnimationFrame(bindScrollReveal);
};

onMounted(refreshPage);
watch(() => route.fullPath, refreshPage);
onBeforeUnmount(() => revealObserver?.disconnect());
</script>
<style>
@import './refined-theme.css';
@import './view/editorial-redesign.css';

:root {
  /* 米白底色与数据大屏同源的蓝色强调 */
  --ink: #18384f;
  --deep: #082c4b;
  --deep-2: #14527a;
  --teal: #087eae;
  --mint: #1ba7ce;
  --lime: #d6a052;
  --paper: #f7f4ec;
  --soft: #e9f1f3;
  --muted: #5d7280;
  --line: #d4e0e4;
  --white: #ffffff;

  /* ===== 阴影：轻盈 ===== */
  --shadow: 0 20px 50px rgba(17, 57, 79, 0.12);
  --shadow-soft: 0 8px 24px rgba(17, 57, 79, 0.08);

  /* ===== 背景渐变 ===== */
  --bg-hero: linear-gradient(160deg, #fbf9f2 0%, #f4f1e9 58%, #eaf1f2 100%);
  --bg-teal: linear-gradient(135deg, #116c9b 0%, #36b8dd 100%);
  --bg-gold: linear-gradient(135deg, #e8bc78 0%, #c99551 100%);
}

* { box-sizing: border-box; }

html { scroll-behavior: auto; }

body {
  margin: 0;
  background: var(--paper);
  color: var(--ink);
  font-family: "Microsoft YaHei", "PingFang SC", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

button, input, select, textarea, a { font: inherit; }
a { color: inherit; }

.page-shell {
  min-height: 100vh;
  overflow: hidden;
  background: var(--bg-hero);
}

/* ===== 通用区块 ===== */
.section {
  width: min(1180px, calc(100% - 48px));
  margin: auto;
  padding: 96px 0;
}

/* ===== 眉题 ===== */
.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: var(--teal);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .2em;
  text-transform: uppercase;
}

.eyebrow:before {
  content: "";
  width: 26px;
  height: 1px;
  background: currentColor;
}

/* ===== 标题 ===== */
.section-heading {
  margin: 13px 0 18px;
  font-size: clamp(32px, 4.2vw, 54px);
  line-height: 1.15;
  letter-spacing: -.035em;
  color: var(--ink);
}

.section-lead {
  max-width: 760px;
  margin: 0;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.95;
}

/* ===== 标签 ===== */
.kicker {
  display: inline-flex;
  padding: 8px 13px;
  border: 1px solid rgba(14, 165, 164, 0.35);
  border-radius: 99px;
  color: var(--teal);
  font-size: 11px;
  letter-spacing: .12em;
  background: rgba(14, 165, 164, 0.06);
}

/* ===== 动画 ===== */
.reveal {
  opacity: 1;
  transform: none;
}

.site-reveal {
  opacity: 0;
  transform: translateY(34px);
  filter: blur(3px);
  transition: opacity .9s cubic-bezier(.2,.75,.25,1),
              transform .9s cubic-bezier(.2,.75,.25,1),
              filter .9s ease;
  will-change: opacity, transform;
}

.site-reveal.site-reveal--visible {
  opacity: 1;
  transform: translateY(0);
  filter: blur(0);
}

@media (prefers-reduced-motion: reduce) {
  .site-reveal,
  .site-reveal.site-reveal--visible {
    opacity: 1;
    transform: none;
    filter: none;
    transition: none;
  }
}

.metric-note {
  font-size: 11px;
  color: var(--muted);
}

/* ===== 标签组 ===== */
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-list span {
  padding: 7px 11px;
  border-radius: 99px;
  background: var(--soft);
  color: var(--teal);
  font-size: 11px;
  font-weight: 700;
}

/* ===== 页脚：深色但明亮 ===== */
.site-footer {
  background: var(--deep);
  color: #a8bcc9;
  padding: 56px 0 34px;
  position: relative;
  overflow: hidden;
}

.site-footer::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #0ea5a4, #fbbf24, #0ea5a4);
  opacity: .9;
}

.site-footer__main {
  width: min(1180px, calc(100% - 48px));
  margin: auto;
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 50px;
}

.site-footer h3 {
  margin: 0 0 12px;
  color: #ffffff;
  font-size: 19px;
}

.site-footer p {
  margin: 7px 0;
  font-size: 12px;
  line-height: 1.7;
}

.site-footer a {
  text-decoration: none;
  transition: color .2s ease;
}

.site-footer a:hover {
  color: var(--mint);
}

.site-footer__links {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px;
}

.site-footer__links a {
  font-size: 12px;
}

.site-footer__bottom {
  width: min(1180px, calc(100% - 48px));
  margin: 35px auto 0;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.10);
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #7a8d9a;
}

/* ===== 响应式 ===== */
@media (max-width: 760px) {
  .section {
    width: min(100% - 28px, 1180px);
    padding: 70px 0;
  }
  .site-footer__main {
    grid-template-columns: 1fr;
    gap: 26px;
  }
  .site-footer__bottom {
    flex-direction: column;
    gap: 7px;
  }
  .section-heading {
    font-size: 34px;
  }
}
</style>
